import { map, prop, sortBy, toPairs } from 'ramda';
import { useEffect, useMemo, useState } from 'react';
import { DefaultTheme, useTheme } from 'styled-components';

export type BreakpointName = keyof DefaultTheme['breakpoints'];

/**
 * Returns the current media breakpoint based on browser size
 */
export const useBreakpoint = () => {
  const { breakpoints, breakpointsMax } = useTheme();
  const { queries, initialMatch } = useMemo(() => {
    const allBreakpoints = map(([name, width]) => ({ name, width }), toPairs(breakpoints));
    const sortedBreakpoints = sortBy(prop('width'), allBreakpoints);
    return sortedBreakpoints.reduce<{
      initialMatch: BreakpointName;
      queries: Array<{ query: MediaQueryList; handler: (event: MediaQueryListEvent) => void }>;
    }>(
      (acc, bp, i, list) => {
        const breakpoint = list[i];
        const query = window.matchMedia(
          `(min-width: ${bp.width}px)${breakpoint ? ` and (max-width: ${breakpointsMax[breakpoint.name]}px)` : ''}`
        );
        const handler = (event: MediaQueryListEvent) => {
          if (event.matches) setCurrentBreakpointName(bp.name);
        };
        return {
          ...acc,
          initialMatch: query.matches ? bp.name : acc.initialMatch,
          queries: [...acc.queries, { query, handler }]
        };
      },
      { initialMatch: 'xxl', queries: [] }
    );
  }, [breakpoints]);

  const [currentBreakpointName, setCurrentBreakpointName] = useState<BreakpointName>(initialMatch);

  useEffect(() => {
    queries.forEach(({ query, handler }) => query.addEventListener('change', handler));
    return () => queries.forEach(({ query, handler }) => query.removeEventListener('change', handler));
  }, [queries]);

  return currentBreakpointName;
};

/**
 * Returns true if the current browser breakpint is less-than a given breakpoint
 *
 * @example
 * // current browser breakpoint is 'md'
 * useBreakpointIsLessThan('lg') // true
 * useBreakpointIsLessThan('md') // false (equal)
 * useBreakpointIsLessThan('sm') // false
 */
export const useBreakpointIsLessThan = (breakpointName: BreakpointName) => {
  const { breakpoints } = useTheme();
  const currentBreakpointName = useBreakpoint();
  return breakpoints[currentBreakpointName] < breakpoints[breakpointName];
};

/**
 * Returns true if the current browser breakpint is greater-than a given breakpoint
 *
 * @example
 * // current browser breakpoint is 'md'
 * useBreakpointIsGreaterThan('lg') // false
 * useBreakpointIsGreaterThan('md') // false (equal)
 * useBreakpointIsGreaterThan('sm') // true
 */
export const useBreakpointIsGreaterThan = (breakpointName: BreakpointName) => {
  const { breakpoints } = useTheme();
  const currentBreakpointName = useBreakpoint();
  return breakpoints[currentBreakpointName] > breakpoints[breakpointName];
};
